import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import Loaders from "components/Common/Loaders";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import {
    fetchUserActivityChart,
    submitUserActivitySummaryFilter,
} from "model/UserActivityModel";
import UserActivityChart from "./UserActivityChart";
import UserActivitySummaryTable from "./UserActivitySummaryTable";
import "./user-activities.scss";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";


const EMPTY_USER_ACTIVITY_FILTERS = {
    userId: "",
    [getUserAsyncFilterLabelFieldKey()]: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    fromDate: "",
    toDate: "",
};

const toIsoDateOrNull = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const UserActivities = () => {
    const [chartLoading, setChartLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [chartPayload, setChartPayload] = useState(null);
    const [summaryRows, setSummaryRows] = useState([]);
    const [filterForm, setFilterForm] = useState(() => ({ ...EMPTY_USER_ACTIVITY_FILTERS }));
    const [requestPayload, setRequestPayload] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });

    const loadUserActivityChart = useCallback(async () => {
        setChartLoading(true);
        try {
            const response = await fetchUserActivityChart({});
            const data = response?.data ?? response ?? {};
            setChartPayload({
                labels: Array.isArray(data?.labels) ? data.labels : [],
                series: Array.isArray(data?.series) ? data.series : [],
            });
        } finally {
            setChartLoading(false);
        }
    }, []);

    const buildSummaryPayload = useCallback(
        (overrides = {}, filterSnapshot = null) => {
            const snapshot = filterSnapshot ?? filterForm;
            const userLabel = String(
                snapshot[getUserAsyncFilterLabelFieldKey()] || ""
            ).trim();

            return {
                nameContains: userLabel,
                search: userLabel,
                userId: snapshot.userId || null,
                fromDate: toIsoDateOrNull(snapshot.fromDate),
                toDate: toIsoDateOrNull(snapshot.toDate),
                clientId: snapshot.clientId || null,
                pageNumber:
                    overrides.pageNumber ??
                    requestPayload.pageNumber ??
                    paginationMeta.pageNumber ??
                    1,
                pageSize:
                    overrides.pageSize ??
                    requestPayload.pageSize ??
                    paginationMeta.pageSize ??
                    100,
                ...overrides,
            };
        },
        [filterForm, paginationMeta.pageNumber, paginationMeta.pageSize, requestPayload]
    );

    const loadUserActivitySummary = useCallback(
        async (overrides = {}, filterSnapshot = null) => {
            setTableLoading(true);
            try {
                const payload = buildSummaryPayload(overrides, filterSnapshot);
                const response = await submitUserActivitySummaryFilter(payload);
                const responseData = response?.data ?? {};

                setSummaryRows(
                    Array.isArray(responseData?.records) ? responseData.records : []
                );
                setPaginationMeta({
                    pageNumber:
                        Number(responseData?.pageNumber) ||
                        Number(payload?.pageNumber) ||
                        1,
                    pageSize:
                        Number(responseData?.pageSize) ||
                        Number(payload?.pageSize) ||
                        100,
                    totalPages: Number(responseData?.totalPages) || 0,
                    totalRecords: Number(responseData?.totalRecords) || 0,
                });
                setRequestPayload(payload);
            } finally {
                setTableLoading(false);
            }
        },
        [buildSummaryPayload]
    );

    useEffect(() => {
        loadUserActivityChart();
    }, [loadUserActivityChart]);

    useEffect(() => {
        loadUserActivitySummary(
            { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
            { ...EMPTY_USER_ACTIVITY_FILTERS }
        );
    }, []);

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previousFilters) => ({
            ...previousFilters,
            [name]: value,
        }));
    };

    const handleSearchSubmit = async (searchValue = filterForm.search, nextForm = filterForm) => {
        await loadUserActivitySummary(
            {
                pageNumber: 1,
                pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
            },
            nextForm
        );
    };

    const handlePageChange = async (page) => {
        await loadUserActivitySummary({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await loadUserActivitySummary({ pageNumber: 1, pageSize: rows });
    };

    return (
        <div className="page-content user-activities-page">
            <Container fluid>
                <Card className="user-activity-chart-card mb-4">
                    <CardBody>
                        <h5 className="fw-bold mb-3">User Activities</h5>
                        <hr />
                        {chartLoading ? (
                            <Loaders />
                        ) : (
                            <UserActivityChart chartPayload={chartPayload} />
                        )}
                    </CardBody>
                </Card>

                <Card className="user-activity-summary-card">
                    <CardBody>
                        <div className="upper-filter-bar manage-user-filter-strip mb-4">
                            <Row className="g-2 g-md-3 align-items-end">
                                <Col xs="12" md="6" lg={3}>
                                    <span className="manage-user-filter-label">User</span>
                                    <AsyncUserSelect
                                        key={`user-activity-user-${filterForm.userId}`}
                                        pageName="UserActivities"
                                        value={filterForm.userId}
                                        initialOption={buildUserAsyncFilterInitialOption(
                                            filterForm.userId,
                                            filterForm[getUserAsyncFilterLabelFieldKey()]
                                        )}
                                        onChange={(option) => {
                                            const nextForm = applyUserAsyncFilterOptionToFormState(
                                                filterForm,
                                                option
                                            );
                                            setFilterForm(nextForm);
                                            if (!option?.value) {
                                                handleSearchSubmit(nextForm);
                                            }
                                        }}
                                        placeholder="Select User"
                                    />
                                </Col>
                                <Col xs="12" md="6" lg={3}>
                                    <span className="manage-user-filter-label">Client</span>
                                    <AsyncClientSelect
                                        key={`user-activity-client-${filterForm.clientId}`}
                                        pageName="ActivityList"
                                        value={filterForm.clientId}
                                        initialOption={buildClientAsyncFilterInitialOption(
                                            filterForm.clientId,
                                            filterForm[getClientAsyncFilterLabelFieldKey()]
                                        )}
                                        onChange={(option) =>
                                            setFilterForm((previousFilters) =>
                                                applyClientAsyncFilterOptionToFormState(
                                                    previousFilters,
                                                    option
                                                )
                                            )
                                        }
                                        placeholder="Select Client"
                                    />
                                </Col>
                                <Col xs="12" md="6" lg={2}>
                                    <span className="manage-user-filter-label">From date</span>
                                    <Input
                                        type="date"
                                        name="fromDate"
                                        value={filterForm.fromDate}
                                        onChange={handleFilterFieldChange}
                                        className="border rounded w-100 category-search-input"
                                    />
                                </Col>
                                <Col xs="12" md="6" lg={2}>
                                    <span className="manage-user-filter-label">To date</span>
                                    <Input
                                        type="date"
                                        name="toDate"
                                        value={filterForm.toDate}
                                        onChange={handleFilterFieldChange}
                                        className="border rounded w-100 category-search-input"
                                    />
                                </Col>
                                <Col xs="12" md="6" lg={2}>
                                    <span className="manage-user-filter-label" aria-hidden="true">
                                        &nbsp;
                                    </span>
                                    <Button
                                        color="primary"
                                        className="w-100 manage-user-search-btn"
                                        onClick={() => handleSearchSubmit(filterForm)}
                                    >
                                        <i className="fa fa-search me-1" aria-hidden="true" />
                                        Search
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        <UserActivitySummaryTable
                            rows={summaryRows}
                            loading={tableLoading}
                            paginationMeta={paginationMeta}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handleRowsPerPageChange}
                        />
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

UserActivities.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(UserActivities);
